/**
 * The FINITE registered launcher actions (#2328 §3, brand amendment).
 *
 * *"Modules may register one of these finite action kinds with a governed
 * route/capability; arbitrary URLs, script actions, icons, or HTML are
 * refused."*
 *
 * This file is that vocabulary, and it is deliberately the whole of it. A host
 * hands the launcher a KIND and a resolved destination; it cannot hand it a
 * label, a description, an icon, a colour or a piece of markup, because none of
 * those are props. The refusal the ruling asks for is therefore STRUCTURAL
 * rather than validated: there is no shape in which an arbitrary tile can be
 * expressed at all.
 *
 * ── the two failure modes are DIFFERENT, and so are the responses ──────────
 *   · an UNKNOWN kind, or a Console-only kind on a Portal surface, is a
 *     programming error about the contract: it RAISES. §4's *"Platform Settings
 *     is never offered or routable in Portal, regardless of mode or persona"* is
 *     not a thing to fail quietly about, and a silent omission there would look
 *     exactly like a correct portal.
 *   · a kind whose destination did NOT resolve is ordinary and expected: it is
 *     OMITTED, with no gap and no placeholder (*"Do not render placeholder/dead
 *     tiles"*). Three of the six have no destination anywhere in this tree
 *     today, which is why the omission path is the well-trodden one.
 */

/* The finite kinds, the surface and the offered-action shape are declared in
   `OnMarbleLauncher.tsx` and imported here as types.
   ─────────────────────────────────────────────────────────────────────────
   They are the COMPONENT's prop contract — what a host may hand the launcher —
   and `conformance/dispositions.test.ts` reads the barrel that way: a type
   published from a file with no adopted value export is dead surface by its
   measurement, and these three are anything but dead. The import is type-only,
   so the cycle back to the component is erased at compile time and there is no
   runtime edge. */
import type { LauncherAction, LauncherActionKind, LauncherSurface } from './OnMarbleLauncher'

export type { LauncherAction, LauncherActionKind, LauncherSurface }

/** How a kind's destination is expressed, which decides how it is resolved. */
export type LauncherDestinationKind = 'route' | 'external'

/**
 * The finite governed glyph vocabulary (#2345 §2).
 *
 * *"Action icons become part of `LAUNCHER_ACTIONS`, not host props. Hosts may
 * still offer only a finite action kind and resolved destination; they cannot
 * inject icon, label, colour, HTML or arbitrary URL."*
 *
 * Named after what each one DRAWS rather than after the kind it is currently
 * assigned to. A vocabulary named `discover | settings | about | …` would be
 * one-to-one with `LauncherActionKind` and therefore not a vocabulary at all —
 * it would be the kind spelled twice, and the first kind that wanted to reuse
 * another's glyph would have to invent a synonym. Which glyph belongs to which
 * action is a JUDGEMENT and is declared once, below, in `LAUNCHER_ACTIONS`
 * (rule 42: derive a restatement, never a judgement).
 *
 * `maker-mark` is the registered `MoonbaseMark` itself, and it is the row-level
 * survivor of the `mark: 'marble' | 'moonbase'` field this replaces: the brand
 * amendment separates *"Marble product identity from Moonbase maker/contact
 * actions"*, and the maker's action still carries the maker's registered asset
 * rather than a generic envelope. The other five are lucide glyphs — the same
 * icon dependency `EntityIcon` already resolves governed entity keys through,
 * so this adds a vocabulary and not a second icon system.
 */
export type LauncherIconKind =
  | 'compass' | 'sliders' | 'sparkles' | 'life-buoy' | 'info' | 'maker-mark'

export interface LauncherActionSpec {
  /** The governed label. The HOST never supplies one. */
  label: string
  /** One governed sentence under the label. */
  description: string
  /** A `route` stays inside the product; `external` leaves it. */
  destination: LauncherDestinationKind
  /** The surfaces on which this kind may be offered AT ALL. */
  surfaces: readonly LauncherSurface[]
  /** The governed glyph. The HOST never supplies one (#2345 §2). */
  icon: LauncherIconKind
}

/**
 * The vocabulary.
 *
 * `platform-settings` is `['console']`, and that single line is §4's rule: the
 * Portal cannot offer it because the surface list does not admit it, rather
 * than because a portal binding remembered to leave it out.
 *
 * `about-onmarble` and `contact-moonbase` carry the two identities the brand
 * amendment separates — *"accessible names distinguish Marble product identity
 * from Moonbase maker/contact actions"*. The accessible names do that (the
 * labels are these), and `icon` keeps the maker's action on the maker's own
 * registered mark rather than a generic glyph.
 */
export const LAUNCHER_ACTIONS: Readonly<Record<LauncherActionKind, LauncherActionSpec>> = {
  discover: {
    label: 'Discover',
    description: 'Solutions and capabilities installed here',
    destination: 'route',
    surfaces: ['console', 'portal'],
    icon: 'compass',
  },
  'platform-settings': {
    label: 'Platform settings',
    description: 'Configure this deployment',
    destination: 'route',
    surfaces: ['console'],
    icon: 'sliders',
  },
  'whats-new': {
    label: "What's new",
    description: 'Recent updates and releases',
    destination: 'route',
    surfaces: ['console', 'portal'],
    icon: 'sparkles',
  },
  help: {
    label: 'Help',
    description: 'Guidance and support',
    destination: 'route',
    surfaces: ['console', 'portal'],
    icon: 'life-buoy',
  },
  'about-onmarble': {
    label: 'About OnMarble',
    description: 'Product identity, version and status',
    destination: 'route',
    surfaces: ['console', 'portal'],
    icon: 'info',
  },
  'contact-moonbase': {
    label: 'Contact Moonbase',
    description: 'Reach the team who built this',
    destination: 'external',
    surfaces: ['console', 'portal'],
    icon: 'maker-mark',
  },
}

/** The order tiles are drawn and TABBED in — one list, so DOM order is linear. */
export const LAUNCHER_ACTION_ORDER: readonly LauncherActionKind[] = [
  'discover', 'platform-settings', 'whats-new', 'help',
  'about-onmarble', 'contact-moonbase',
]

/**
 * Is a string a destination this kit will send a person to?
 *
 * HTTPS ONLY, PARSED rather than pattern-matched. The amendment asks for an
 * *"HTTPS/route allowlist"* on the contact destination, and the reason it is
 * checked here as well as at the registration door is that this is the last
 * place before a person's browser: `javascript:`, `data:text/html` and
 * `mailto:` all read as plausible strings in a settings field, and two of the
 * three execute.
 *
 * A malformed value returns false rather than throwing — an unreachable contact
 * destination is the omission case, not a broken shell.
 */
export function isSafeExternalDestination(href: string | null | undefined): boolean {
  if (typeof href !== 'string' || href.trim() === '') return false
  try {
    return new URL(href).protocol === 'https:'
  } catch {
    return false
  }
}

/** One action, resolved: what the launcher actually draws. */
export interface ResolvedLauncherAction {
  kind: LauncherActionKind
  spec: LauncherActionSpec
  onSelect?: (() => void) | null
  href?: string | null
}

/**
 * Which of the offered actions this surface draws, in governed order.
 *
 * RAISES on a kind the vocabulary does not hold, on a kind this surface may not
 * offer, and on a kind offered twice. OMITS a kind whose destination did not
 * resolve.
 */
export function resolveLauncherActions(
  offered: readonly LauncherAction[],
  surface: LauncherSurface,
  mode: 'brand' | 'actions',
): ResolvedLauncherAction[] {
  const byKind = new Map<LauncherActionKind, LauncherAction>()
  for (const action of offered) {
    const spec = LAUNCHER_ACTIONS[action.kind] as LauncherActionSpec | undefined
    if (spec === undefined) {
      throw new Error(
        `<OnMarbleLauncher> was offered an unregistered action kind ${JSON.stringify(action.kind)}. `
        + `The finite vocabulary is ${LAUNCHER_ACTION_ORDER.join(', ')}; arbitrary destinations are refused.`,
      )
    }
    if (!spec.surfaces.includes(surface)) {
      throw new Error(
        `<OnMarbleLauncher> was offered "${action.kind}" on the ${surface} surface, which may not `
        + 'offer it. Platform settings is Console-only under every mode and every persona (#2328 §4).',
      )
    }
    if (byKind.has(action.kind)) {
      throw new Error(`<OnMarbleLauncher> was offered "${action.kind}" twice; a kind is registered once.`)
    }
    byKind.set(action.kind, action)
  }
  /* `brand` is the lockup plus About OnMarble and nothing else (#2328 §4, B3
     ruling §5). Filtered HERE rather than at the call site, so a portal cannot
     widen its own brand mode by passing more. */
  const admitted = mode === 'brand'
    ? LAUNCHER_ACTION_ORDER.filter((kind) => kind === 'about-onmarble')
    : LAUNCHER_ACTION_ORDER
  const resolved: ResolvedLauncherAction[] = []
  for (const kind of admitted) {
    const action = byKind.get(kind)
    if (action === undefined) continue
    const spec = LAUNCHER_ACTIONS[kind]
    if (spec.destination === 'external') {
      if (!isSafeExternalDestination(action.href)) continue
      resolved.push({ kind, spec, href: action.href })
      continue
    }
    if (typeof action.onSelect !== 'function') continue
    resolved.push({ kind, spec, onSelect: action.onSelect })
  }
  return resolved
}
